import LinkIcon from '@mui/icons-material/Link';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, useMediaQuery, Box, Typography, Divider, Paper, TableContainer, Card, CardHeader, CardContent, CardActions, Button } from '@mui/material';
import { Display, MapURLInformation } from 'adoms-common-lib';
import React from 'react';
import { useTheme } from '@mui/material/styles';


type MapURLListTableProps = {
    mapURLInfoList: MapURLInformation[];
    setSnackBarOpen: React.Dispatch<React.SetStateAction<boolean>>;
    handleEditMapURL: (mapURLInfo: MapURLInformation) => void;
    handleDeleteMapURL: (mapURLInfo: MapURLInformation) => void;
};

/**
 * マップURL情報リストテーブル表示用コンポーネント
 * @param props:参照用プロパティ
 */
export const MapURLListTable: React.FC<MapURLListTableProps> = (props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    /**
     * PC用のTable Bodyを表示する
     * @param mapURLInfo 
     * @returns 
     */
    const displayPcTableBody = (mapURLInfo: MapURLInformation) => {
        const createDateTime = Display.getDateTimeStringFromISO8601(mapURLInfo.createDateTime);
        const expirationDateTime = Display.getDateTimeStringFromISO8601(mapURLInfo.expirationDateTime);
        const handleCopyToClipboard = async () => {
            if (mapURLInfo.url) {
                await global.navigator.clipboard.writeText(mapURLInfo.url);
                props.setSnackBarOpen(true);
            }
        };

        const handleEdit = () => {
            props.handleEditMapURL(mapURLInfo);
        };

        const handleDelete = () => {
            props.handleDeleteMapURL(mapURLInfo);
        };

        return (
            <TableRow key={mapURLInfo.url}>
                <TableCell>
                    <Tooltip title="URLをコピー">
                        <IconButton
                            aria-label="copy url"
                            color="primary"
                            size="small"
                            onClick={
                                () => handleCopyToClipboard()
                            }
                        >
                            <LinkIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </TableCell>
                <TableCell sx={{ minWidth: 150 }}>
                    {mapURLInfo.urlId}
                </TableCell>
                <TableCell sx={{ minWidth: 150 }}>{expirationDateTime}</TableCell>
                <TableCell sx={{ minWidth: 150 }}>{createDateTime}</TableCell>
                <TableCell sx={{ minWidth: 120 }}>{mapURLInfo.issueUser.name}</TableCell>
                <TableCell sx={{ minWidth: 150 }}>{mapURLInfo.businessPartnerId}</TableCell>
                <TableCell sx={{ minWidth: 150 }}>{mapURLInfo.remarks}</TableCell>
                <TableCell
                    sx={{
                        textAlign: "right",
                        minWidth: 120
                    }}   
                >
                    <Tooltip title="編集">
                        <IconButton
                            aria-label="edit url"
                            color="primary"
                            size="medium"
                            onClick={handleEdit}
                        >
                            <EditIcon fontSize="medium" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="削除">
                        <IconButton
                            aria-label="delete url"
                            color="primary"
                            size="medium"
                            onClick={handleDelete}
                        >
                            <DeleteIcon fontSize="medium" />
                        </IconButton>
                    </Tooltip>
                </TableCell>
            </TableRow>
        );
    };

    /**
     * モバイル用のリスト項目を表示する
     * @param mapURLInfo 
     * @returns 
     */
    const displayMobileListItem = (mapURLInfo: MapURLInformation) => {
        const createDateTime = Display.getDateTimeStringFromISO8601(mapURLInfo.createDateTime);
        const expirationDateTime = Display.getDateTimeStringFromISO8601(mapURLInfo.expirationDateTime);

        const handleCopyToClipboard = async () => {
            if (mapURLInfo.url) {
                await global.navigator.clipboard.writeText(mapURLInfo.url);
                props.setSnackBarOpen(true);
            }
        };

        const handleEdit = () => {
            props.handleEditMapURL(mapURLInfo);
        };

        const handleDelete = () => {
            props.handleDeleteMapURL(mapURLInfo);
        };

        const InfoRow = ({ label, value }: { label: string, value: React.ReactNode }) => (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, alignItems: 'flex-start' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 2, whiteSpace: 'nowrap' }}>{label}</Typography>
                <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-word' }}>{value}</Typography>
            </Box>
        );

        return (
            <Card key={mapURLInfo.urlId} sx={{ mb: 2 }}>
                <CardHeader
                    title={`ID: ${mapURLInfo.urlId}`}
                    titleTypographyProps={{ variant: 'subtitle1', sx: { wordBreak: 'break-all' } }}
                    sx={{ pb: 0, alignItems: 'flex-start' }}
                />
                <CardContent sx={{ pt: 1 }}>
                    <InfoRow label="有効期限" value={expirationDateTime} />
                    <InfoRow label="発行日時" value={createDateTime} />
                    <InfoRow label="発行者" value={mapURLInfo.issueUser.name} />
                    <InfoRow label="ビジネスパートナーID" value={mapURLInfo.businessPartnerId} />
                    <InfoRow label="備考" value={mapURLInfo.remarks} />
                </CardContent>
                <CardActions sx={{ pt: 0 }}>
                    <Tooltip title="URLをコピー">
                        <IconButton aria-label="copy url" color="primary" size="small" onClick={handleCopyToClipboard}>
                            <LinkIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="編集">
                        <IconButton aria-label="edit url" color="primary" size="small" onClick={handleEdit}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="削除">
                        <IconButton aria-label="delete url" color="primary" size="small" onClick={handleDelete}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </CardActions>
            </Card>
        );
    };

    if (props.mapURLInfoList.length === 0) {
        return <Alert severity="info">URLがありません。</Alert>;
    }

    if (isMobile) {
        return (
            <Box>
                {props.mapURLInfoList.map((mapURLInfo) => displayMobileListItem(mapURLInfo))}
            </Box>
        );
    }

    return (
        <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="map-url-info-table">
                <TableHead>
                    <TableRow>
                        <TableCell >
                            <LinkIcon sx={{ marginLeft: "5px", marginTop: "6px" }} fontSize="small" />
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>ID</TableCell>
                        <TableCell sx={{ minWidth: 150 }}>有効期限</TableCell>
                        <TableCell sx={{ minWidth: 150 }}>発行日時</TableCell>
                        <TableCell sx={{ minWidth: 120 }}>発行者</TableCell>
                        <TableCell sx={{ minWidth: 150 }}>ビジネスパートナーID</TableCell>
                        <TableCell sx={{ minWidth: 150 }}>備考</TableCell>
                        <TableCell sx={{ minWidth: 120 }}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {props.mapURLInfoList.map((mapURLInfo) => displayPcTableBody(mapURLInfo))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};